import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  previewBundle,
  createBundle,
  getAdOpsStatus,
  getAuditLog,
  refreshKeywords,
  getRefreshStatus,
  CacheColdError,
  runDiagnostics,
  matchKeyword,
  testPqlFilter,
  testContentFilter,
  BundlePreviewResponse,
  BundleAuditLogEntry,
  BundleCreateResponse,
  Phase0DiagnosticsResult,
  PqlFilterProbeResult,
  ContentPqlProbeResult,
  DiagnosticStatus,
  RefreshStatus,
} from '../../services/adOpsApi';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { StatusPill, type StatusVariant, Button, tableStyles } from '../ui';
import KeywordChip from './BundleGenerator/KeywordChip';
import VideoPreviewTable from './BundleGenerator/VideoPreviewTable';
import BundleSuccessCard from './BundleGenerator/BundleSuccessCard';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

const formatElapsed = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const formatAgo = (ts: number): string => {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ago`;
};

const SCOPE_LABELS: Record<string, string> = {
  keywords: 'keywords',
  category: 'categories',
  yt_channel_id: 'channels',
};

/** "12,500 keywords · 84 categories · 152 channels" — falls back to total
 * when older backends omit `countsByScope`. */
const formatScopeCounts = (
  byScope: RefreshStatus['countsByScope'],
  total: number,
): string => {
  if (!byScope) return `${total.toLocaleString()} values`;
  const parts: string[] = [];
  for (const scope of Object.keys(SCOPE_LABELS)) {
    const n = byScope[scope as keyof typeof byScope];
    if (typeof n === 'number') parts.push(`${n.toLocaleString()} ${SCOPE_LABELS[scope]}`);
  }
  return parts.length > 0 ? parts.join(' · ') : `${total.toLocaleString()} values`;
};

const DIAG_TO_PILL: Record<DiagnosticStatus, StatusVariant> = {
  pass: 'live',
  warn: 'pending',
  fail: 'flagged',
  skipped: 'draft',
};

type Phase = 'idle' | 'expanding' | 'previewed' | 'creating' | 'created' | 'error';

/** How many videos to reveal per click of the "Show more" button. The backend
 * ships up to 200 already-fetched videos in `preview.sampleVideos`; this is
 * pure display pagination so the table doesn't dump everything at once. */
const VIDEO_PAGE_SIZE = 20;

const BundleGeneratorTab: React.FC = () => {
  const [topic, setTopic] = useState('');
  const [targetMarketsRaw, setTargetMarketsRaw] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [dryRun, setDryRun] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const [preview, setPreview] = useState<BundlePreviewResponse | null>(null);
  const [removedKeywords, setRemovedKeywords] = useState<Set<string>>(new Set());
  const [selectedContentIds, setSelectedContentIds] = useState<Set<string>>(new Set());
  const [visibleVideoCount, setVisibleVideoCount] = useState<number>(VIDEO_PAGE_SIZE);
  const [bundleName, setBundleName] = useState('');
  const [description, setDescription] = useState('');

  const [audit, setAudit] = useState<BundleAuditLogEntry[]>([]);
  const [auditOpen, setAuditOpen] = useState(false);

  const [createdBundle, setCreatedBundle] = useState<BundleCreateResponse | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const [addedKeywords, setAddedKeywords] = useState<string[]>([]);
  const [customChipInput, setCustomChipInput] = useState('');
  const [customChipChecking, setCustomChipChecking] = useState(false);
  const [customChipError, setCustomChipError] = useState<string | null>(null);

  const [refreshStatus, setRefreshStatus] = useState<RefreshStatus | null>(null);
  // Live ticking clock so the elapsed-time display updates while refreshing.
  const [nowTick, setNowTick] = useState(Date.now());

  const [successUrl, setSuccessUrl] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [diagOpen, setDiagOpen] = useState(false);
  const [diagBundleId, setDiagBundleId] = useState('');
  const [diagRunning, setDiagRunning] = useState(false);
  const [diagResult, setDiagResult] = useState<Phase0DiagnosticsResult | null>(null);
  const [diagError, setDiagError] = useState<string | null>(null);

  const [pqlProbeTerm, setPqlProbeTerm] = useState('psg');
  const [pqlProbeRunning, setPqlProbeRunning] = useState(false);
  const [pqlProbeResult, setPqlProbeResult] = useState<PqlFilterProbeResult | null>(null);
  const [pqlProbeError, setPqlProbeError] = useState<string | null>(null);

  const [contentProbeRunning, setContentProbeRunning] = useState(false);
  const [contentProbeResult, setContentProbeResult] = useState<ContentPqlProbeResult | null>(null);
  const [contentProbeError, setContentProbeError] = useState<string | null>(null);

  // Permission/status echo
  useEffect(() => {
    let cancelled = false;
    getAdOpsStatus()
      .then((s) => {
        if (cancelled) return;
        setDryRun(s.dryRun);
        setIsAdmin(s.isAdmin);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Status check failed.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-warm the keyword cache when the tab first loads.
  useEffect(() => {
    let cancelled = false;
    getRefreshStatus()
      .then((status) => {
        if (cancelled) return;
        setRefreshStatus(status);
        if (status.state === 'idle' && status.count === 0) {
          refreshKeywords()
            .then((next) => !cancelled && setRefreshStatus(next))
            .catch(() => undefined); // missing manage perm is non-fatal
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Poll refresh status while a job is in flight. Re-fetch cache on completion,
  // surface an error on failure, time out after 5 minutes.
  const pollStartRef = useRef<number | null>(null);
  useEffect(() => {
    if (refreshStatus?.state !== 'refreshing') {
      pollStartRef.current = null;
      return;
    }
    if (pollStartRef.current == null) pollStartRef.current = Date.now();

    const tickInterval = setInterval(() => setNowTick(Date.now()), 1000);

    const interval = setInterval(async () => {
      if (Date.now() - (pollStartRef.current ?? Date.now()) > POLL_TIMEOUT_MS) {
        setRefreshStatus((s) =>
          s ? { ...s, state: 'error', error: 'Refresh timed out; please retry.' } : s,
        );
        return;
      }
      try {
        const next = await getRefreshStatus();
        setRefreshStatus(next);
      } catch {
        // transient — keep polling
      }
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      clearInterval(tickInterval);
    };
  }, [refreshStatus?.state]);

  // Surface a one-shot success toast when the anchor-sample fetch completes.
  const lastFetchedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (refreshStatus?.state !== 'idle' || !refreshStatus.fetchedAt) return;
    if (lastFetchedAtRef.current === refreshStatus.fetchedAt) return;
    if (lastFetchedAtRef.current != null) {
      const parts = formatScopeCounts(refreshStatus.countsByScope, refreshStatus.count);
      setSuccessMessage(`Loaded anchor samples: ${parts}.`);
    }
    lastFetchedAtRef.current = refreshStatus.fetchedAt;
  }, [refreshStatus?.state, refreshStatus?.fetchedAt, refreshStatus?.count, refreshStatus?.countsByScope]);

  // Load audit log on first open of that section
  useEffect(() => {
    if (!auditOpen) return;
    getAuditLog(20)
      .then((r) => setAudit(r.entries))
      .catch((err) => setError(err?.message || 'Failed to load audit log.'));
  }, [auditOpen]);

  const handleExpand = useCallback(async () => {
    if (!topic.trim()) {
      setError('Enter a topic first.');
      return;
    }
    setError(null);
    setPhase('expanding');
    setSuccessUrl(null);
    setSuccessMessage(null);
    try {
      const targetMarkets = targetMarketsRaw
        .split(',')
        .map((m) => m.trim())
        .filter((m) => m.length > 0);
      const result = await previewBundle({
        topic: topic.trim(),
        targetMarkets: targetMarkets.length > 0 ? targetMarkets : undefined,
      });
      setPreview(result);
      setRemovedKeywords(new Set());
      // Pre-select only the first visible page. Hidden videos can be revealed
      // via "Show more" and opted in deliberately.
      setSelectedContentIds(
        new Set(result.sampleVideos.slice(0, VIDEO_PAGE_SIZE).map((v) => v.contentId)),
      );
      setVisibleVideoCount(VIDEO_PAGE_SIZE);
      setBundleName(result.suggestedBundleName);
      setAddedKeywords([]);
      setCustomChipInput('');
      setCustomChipError(null);
      setPhase('previewed');
    } catch (err: any) {
      if (err instanceof CacheColdError) {
        setRefreshStatus(err.refreshStatus);
        setError('Loading GAM keywords first — try again in a minute.');
      } else {
        setError(err?.message || 'Preview failed.');
      }
      setPhase('error');
    }
  }, [topic, targetMarketsRaw]);

  const toggleChip = (gamKeyword: string) => {
    setRemovedKeywords((prev) => {
      const next = new Set(prev);
      if (next.has(gamKeyword)) next.delete(gamKeyword);
      else next.add(gamKeyword);
      return next;
    });
  };

  const toggleVideo = (contentId: string) => {
    setSelectedContentIds((prev) => {
      const next = new Set(prev);
      if (next.has(contentId)) next.delete(contentId);
      else next.add(contentId);
      return next;
    });
  };

  const selectAllVideos = () => {
    if (!preview) return;
    setSelectedContentIds(new Set(preview.sampleVideos.map((v) => v.contentId)));
  };

  const clearAllVideos = () => setSelectedContentIds(new Set());

  const handleRefreshKeywords = useCallback(async () => {
    setError(null);
    try {
      const status = await refreshKeywords();
      setRefreshStatus(status);
    } catch (err: any) {
      setError(err?.message || 'Refresh failed to start.');
    }
  }, []);

  const handleCreate = useCallback(async () => {
    if (!preview) return;
    const name = bundleName.trim();
    if (!name) {
      setError('Bundle name is required.');
      return;
    }
    const contentIds = Array.from(selectedContentIds);
    if (contentIds.length === 0) {
      setError('Select at least one video.');
      return;
    }

    const allKeywords = [...preview.matchedKeywords, ...addedKeywords];
    const activeKeywords = allKeywords.filter((k) => !removedKeywords.has(k));

    setPhase('creating');
    setError(null);
    setCopyState('idle');
    try {
      const r = await createBundle({
        name,
        description: description.trim() || undefined,
        contentIds,
        source: {
          topic: topic.trim(),
          expandedKeywords: preview.expandedKeywords,
          matchedKeywords: activeKeywords,
        },
      });
      setCreatedBundle(r);
      setSuccessMessage(null);
      setSuccessUrl(null);
      setPhase('created');
      if (auditOpen) {
        getAuditLog(20)
          .then((res) => setAudit(res.entries))
          .catch(() => undefined);
      }
    } catch (err: any) {
      if (err?.status === 409) {
        setError(`A bundle named "${name}" already exists. Pick a different name.`);
      } else {
        setError(err?.message || 'Create failed.');
      }
      setPhase('previewed');
    }
  }, [preview, bundleName, description, selectedContentIds, removedKeywords, addedKeywords, topic, auditOpen]);

  const handleAddChip = useCallback(async () => {
    const term = customChipInput.trim();
    if (!term) return;
    setCustomChipError(null);
    setCustomChipChecking(true);
    try {
      const result = await matchKeyword(term);
      if (!result.matched) {
        setCustomChipError(`"${term}" isn't in the GAM keyword index.`);
        return;
      }
      const gk = result.matched.gamKeyword;
      const alreadyIn =
        (preview?.matchedKeywords.includes(gk) ?? false) || addedKeywords.includes(gk);
      if (alreadyIn) {
        setCustomChipError(`"${gk}" is already in the chip set.`);
        return;
      }
      setAddedKeywords((prev) => [...prev, gk]);
      setCustomChipInput('');
      setRemovedKeywords((prev) => {
        if (!prev.has(gk)) return prev;
        const next = new Set(prev);
        next.delete(gk);
        return next;
      });
    } catch (err: any) {
      if (err instanceof CacheColdError) {
        setRefreshStatus(err.refreshStatus);
        setCustomChipError('Keyword cache is loading — try again in a minute.');
      } else {
        setCustomChipError(err?.message || 'Lookup failed.');
      }
    } finally {
      setCustomChipChecking(false);
    }
  }, [customChipInput, preview, addedKeywords]);

  const handleCopyContentIds = useCallback(async () => {
    if (!createdBundle) return;
    const text = createdBundle.suggestedContentIds.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  }, [createdBundle]);

  // Auto-reset the "✓ Copied" label after 2.5s. Using an effect (instead of an
  // inline setTimeout in handleCopyContentIds) means unmount or a Reset that
  // clears copyState aborts the timer naturally — no leaked dispatch into a
  // stale tree.
  useEffect(() => {
    if (copyState !== 'copied') return;
    const timer = setTimeout(() => setCopyState('idle'), 2500);
    return () => clearTimeout(timer);
  }, [copyState]);

  const handleRunDiagnostics = useCallback(async () => {
    const id = diagBundleId.trim();
    if (!id) {
      setDiagError('Paste an existing GAM bundle ID first.');
      return;
    }
    setDiagError(null);
    setDiagResult(null);
    setDiagRunning(true);
    try {
      const result = await runDiagnostics(id);
      setDiagResult(result);
    } catch (err: any) {
      setDiagError(err?.message || 'Diagnostics failed.');
    } finally {
      setDiagRunning(false);
    }
  }, [diagBundleId]);

  const handleRunPqlProbe = useCallback(async () => {
    const term = pqlProbeTerm.trim();
    if (!term) {
      setPqlProbeError('Enter a term to probe with.');
      return;
    }
    setPqlProbeError(null);
    setPqlProbeResult(null);
    setPqlProbeRunning(true);
    try {
      const result = await testPqlFilter(term);
      setPqlProbeResult(result);
    } catch (err: any) {
      setPqlProbeError(err?.message || 'PQL probe failed.');
    } finally {
      setPqlProbeRunning(false);
    }
  }, [pqlProbeTerm]);

  const handleRunContentProbe = useCallback(async () => {
    setContentProbeError(null);
    setContentProbeResult(null);
    setContentProbeRunning(true);
    try {
      const result = await testContentFilter();
      setContentProbeResult(result);
    } catch (err: any) {
      setContentProbeError(err?.message || 'Content PQL probe failed.');
    } finally {
      setContentProbeRunning(false);
    }
  }, []);

  const handleReset = () => {
    setTopic('');
    setTargetMarketsRaw('');
    setPreview(null);
    setRemovedKeywords(new Set());
    setSelectedContentIds(new Set());
    setBundleName('');
    setDescription('');
    setPhase('idle');
    setError(null);
    setSuccessUrl(null);
    setSuccessMessage(null);
    setCreatedBundle(null);
    setCopyState('idle');
    setAddedKeywords([]);
    setCustomChipInput('');
    setCustomChipError(null);
  };

  return (
    <div style={styles.root}>
      {dryRun && (
        <div style={styles.dryRunBanner}>
          <AlertTriangle size={14} strokeWidth={1.5} style={{ verticalAlign: '-2px', marginRight: 6 }} />
          <strong>Dry-run mode is ON.</strong> Bundle creates are simulated — no writes hit GAM.
        </div>
      )}

      {refreshStatus && (
        <div
          style={
            refreshStatus.state === 'error'
              ? styles.errorBanner
              : refreshStatus.state === 'refreshing'
              ? styles.cacheBannerInfo
              : refreshStatus.count === 0
              ? styles.dryRunBanner
              : styles.cacheBannerSubtle
          }
        >
          <StatusPill
            variant={
              refreshStatus.state === 'refreshing'
                ? 'pending'
                : refreshStatus.state === 'error'
                ? 'flagged'
                : refreshStatus.count > 0
                ? 'live'
                : 'draft'
            }
            noDot={refreshStatus.state !== 'refreshing'}
          >
            {refreshStatus.state === 'refreshing'
              ? 'loading'
              : refreshStatus.state === 'error'
              ? 'error'
              : refreshStatus.count > 0
              ? 'ready'
              : 'empty'}
          </StatusPill>{' '}
          {refreshStatus.state === 'refreshing' ? (
            <>
              Loading GAM anchor samples…
              {refreshStatus.startedAt &&
                ` Elapsed: ${formatElapsed(nowTick - refreshStatus.startedAt)}.`}
            </>
          ) : refreshStatus.state === 'error' ? (
            <>
              <strong>Anchor sample fetch failed:</strong> {refreshStatus.error ?? 'Unknown error.'}{' '}
              <Button variant="ghost" size="sm" onClick={handleRefreshKeywords}>
                Retry
              </Button>
            </>
          ) : refreshStatus.count > 0 ? (
            <>
              Anchor sample: <strong>{formatScopeCounts(refreshStatus.countsByScope, refreshStatus.count)}</strong>
              {refreshStatus.fetchedAt && `, refreshed ${formatAgo(refreshStatus.fetchedAt)}`}.
              {' '}Per-candidate matching runs on-demand against GAM.
            </>
          ) : (
            <>
              Anchor cache is empty —{' '}
              <Button variant="ghost" size="sm" onClick={handleRefreshKeywords}>
                load samples
              </Button>{' '}
              to enable previews.
            </>
          )}
        </div>
      )}

      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <h2 style={styles.sectionTitle}>Topic</h2>
            <p style={styles.sectionSubtitle}>
              Describe the campaign topic — the agent will expand it into matching GAM keyword phrases.
            </p>
          </div>
          <Button
            variant="ghost"
            size="md"
            onClick={handleRefreshKeywords}
            disabled={refreshStatus?.state === 'refreshing'}
            title="Re-fetch the anchor samples GAM uses to ground the LLM prompt."
          >
            {refreshStatus?.state === 'refreshing'
              ? `Loading… (${formatElapsed(nowTick - (refreshStatus.startedAt ?? nowTick))})`
              : 'Reload anchor samples'}
          </Button>
        </div>

        <textarea
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder='e.g. "champions league football", "adidas + youth basketball", "iShowSpeed Madrid trip"'
          rows={3}
          style={styles.textarea}
        />

        <div style={styles.inlineRow}>
          <label style={styles.inlineLabel}>Target markets (optional, comma-separated)</label>
          <input
            type="text"
            value={targetMarketsRaw}
            onChange={(e) => setTargetMarketsRaw(e.target.value)}
            placeholder="US, MX"
            style={styles.input}
          />
        </div>

        <div style={styles.buttonRow}>
          <button
            onClick={handleExpand}
            disabled={
              phase === 'expanding' ||
              phase === 'creating' ||
              !topic.trim() ||
              refreshStatus?.state === 'refreshing' ||
              (refreshStatus?.state === 'idle' && (refreshStatus?.count ?? 0) === 0)
            }
            style={styles.primaryButton}
            title={
              refreshStatus?.state === 'refreshing'
                ? 'Waiting for keyword cache to finish loading…'
                : refreshStatus?.state === 'idle' && (refreshStatus?.count ?? 0) === 0
                ? 'Keyword cache is empty — refresh first.'
                : undefined
            }
          >
            {phase === 'expanding' ? 'Expanding…' : 'Expand & preview'}
          </button>
          {(phase === 'previewed' || phase === 'created' || phase === 'error') && (
            <button onClick={handleReset} style={styles.secondaryButton}>
              Start over
            </button>
          )}
        </div>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}
      {successMessage && !createdBundle && (
        <div style={styles.successBanner}>
          <CheckCircle2 size={14} strokeWidth={1.5} style={{ verticalAlign: '-2px', marginRight: 6 }} />
          {successMessage}
          {successUrl && (
            <>
              {' '}
              <a href={successUrl} target="_blank" rel="noreferrer" style={styles.successLink}>
                Open in GAM →
              </a>
            </>
          )}
        </div>
      )}

      {createdBundle && (
        <BundleSuccessCard
          bundle={createdBundle}
          copyState={copyState}
          onCopyContentIds={handleCopyContentIds}
          onReset={handleReset}
        />
      )}

      {preview && (
        <>
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <div>
                <h2 style={styles.sectionTitle}>Matched keywords</h2>
                <p style={styles.sectionSubtitle}>
                  {preview.matchedKeywords.length + addedKeywords.length - removedKeywords.size} /{' '}
                  {preview.matchedKeywords.length + addedKeywords.length} kept ·{' '}
                  {addedKeywords.length > 0 && `${addedKeywords.length} added · `}
                  {preview.droppedKeywords.length} dropped (not in GAM index) ·{' '}
                  {preview.matchedContentCount} video matches
                  {preview.truncated && ' (capped — narrow the topic to see the true total)'}
                </p>
              </div>
            </div>

            <div style={styles.chipWrap}>
              {preview.matchedKeywords.length === 0 && addedKeywords.length === 0 ? (
                <span style={styles.emptyText}>
                  No keywords matched the GAM index. Try a different topic, or add one manually below.
                </span>
              ) : (
                [...preview.matchedKeywords, ...addedKeywords].map((k) => (
                  <KeywordChip
                    key={k}
                    keyword={k}
                    removed={removedKeywords.has(k)}
                    isAdded={addedKeywords.includes(k)}
                    onToggle={() => toggleChip(k)}
                  />
                ))
              )}
            </div>

            <div style={styles.chipAddRow}>
              <input
                type="text"
                value={customChipInput}
                onChange={(e) => setCustomChipInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddChip();
                  }
                }}
                placeholder="Add a keyword that's already in GAM (Enter to add)"
                style={styles.chipAddInput}
                disabled={customChipChecking}
              />
              <button
                onClick={handleAddChip}
                disabled={customChipChecking || !customChipInput.trim()}
                style={styles.secondaryButton}
              >
                {customChipChecking ? 'Checking…' : 'Add'}
              </button>
            </div>
            {customChipError && (
              <div style={styles.chipAddError}>{customChipError}</div>
            )}

            {preview.droppedKeywords.length > 0 && (
              <details style={styles.dropDetails}>
                <summary style={styles.dropSummary}>
                  Show {preview.droppedKeywords.length} dropped candidate(s)
                </summary>
                <div style={styles.dropList}>
                  {preview.droppedKeywords.map((d) => (
                    <span key={d} style={styles.dropChip}>{d}</span>
                  ))}
                </div>
              </details>
            )}
          </div>

          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <div>
                <h2 style={styles.sectionTitle}>Sample videos</h2>
                <p style={styles.sectionSubtitle}>
                  {selectedContentIds.size} selected · showing{' '}
                  {Math.min(visibleVideoCount, preview.sampleVideos.length)} of{' '}
                  {preview.sampleVideos.length}
                  {preview.truncated ? '+' : ''}.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={selectAllVideos} style={styles.linkButton}>Select all</button>
                <button onClick={clearAllVideos} style={styles.linkButton}>Clear</button>
              </div>
            </div>

            <VideoPreviewTable
              videos={preview.sampleVideos.slice(0, visibleVideoCount)}
              selectedContentIds={selectedContentIds}
              onToggleVideo={toggleVideo}
            />

            {visibleVideoCount < preview.sampleVideos.length && (
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                <button
                  onClick={() =>
                    setVisibleVideoCount((c) =>
                      Math.min(c + VIDEO_PAGE_SIZE, preview.sampleVideos.length),
                    )
                  }
                  style={styles.secondaryButton}
                >
                  Show next{' '}
                  {Math.min(VIDEO_PAGE_SIZE, preview.sampleVideos.length - visibleVideoCount)}
                </button>
              </div>
            )}
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Create bundle</h2>
            <p style={styles.sectionSubtitle}>
              Review the name. The bundle will be created with the selected videos above.
            </p>
            <div style={styles.inlineRow}>
              <label style={styles.inlineLabel}>Bundle name *</label>
              <input
                type="text"
                value={bundleName}
                onChange={(e) => setBundleName(e.target.value)}
                style={styles.input}
                placeholder="Suggested name will appear here"
              />
            </div>
            <div style={styles.inlineRow}>
              <label style={styles.inlineLabel}>Description (optional)</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.buttonRow}>
              <button
                onClick={handleCreate}
                disabled={
                  phase === 'creating' ||
                  !bundleName.trim() ||
                  selectedContentIds.size === 0
                }
                style={styles.primaryButton}
              >
                {phase === 'creating' ? 'Creating…' : `Create bundle (${selectedContentIds.size} videos)`}
              </button>
            </div>
          </div>
        </>
      )}

      {isAdmin && (
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <h2 style={styles.sectionTitle}>Phase 0 diagnostics <span style={styles.adminTag}>admin</span></h2>
            <p style={styles.sectionSubtitle}>
              Paste an existing GAM bundle ID (from Video → Content → Content bundles in GAM)
              to validate the SOAP contract read-only. Nothing is written to GAM.
            </p>
          </div>
          <button onClick={() => setDiagOpen((v) => !v)} style={styles.linkButton}>
            {diagOpen ? 'Hide' : 'Show'}
          </button>
        </div>
        {diagOpen && (
          <>
            <div style={styles.inlineRow}>
              <label style={styles.inlineLabel}>Existing bundle ID</label>
              <input
                type="text"
                value={diagBundleId}
                onChange={(e) => setDiagBundleId(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="e.g. 1234567890"
                style={styles.input}
                inputMode="numeric"
              />
              <button
                onClick={handleRunDiagnostics}
                disabled={diagRunning || !diagBundleId.trim()}
                style={styles.primaryButton}
              >
                {diagRunning ? 'Running…' : 'Run checks'}
              </button>
            </div>
            {diagError && <div style={{ ...styles.errorBanner, marginTop: 12 }}>{diagError}</div>}
            {diagResult && (
              <div style={{ marginTop: 16 }}>
                <div style={diagResult.overall === 'pass' ? styles.diagOverallPass : styles.diagOverallFail}>
                  Overall: <strong>{diagResult.overall.toUpperCase()}</strong>
                  {diagResult.samples.bundleContentIdField && (
                    <> · Bundle uses field <code>{diagResult.samples.bundleContentIdField}</code></>
                  )}
                </div>
                <ul style={styles.diagList}>
                  {diagResult.checks.map((c) => (
                    <li key={c.id} style={styles.diagItem}>
                      <StatusPill variant={DIAG_TO_PILL[c.status]} noDot>
                        {c.status}
                      </StatusPill>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{c.label}</div>
                        <div style={styles.subText}>{c.details}</div>
                        {c.payloadPreview != null && (
                          <details style={{ marginTop: 6 }}>
                            <summary style={styles.dropSummary}>Show payload</summary>
                            <pre style={styles.diagPre}>{JSON.stringify(c.payloadPreview, null, 2)}</pre>
                          </details>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border-1)' }}>
              <h3 style={{ ...styles.sectionTitle, fontSize: 14 }}>
                PQL `name LIKE` filter probe
              </h3>
              <p style={styles.sectionSubtitle}>
                Tests whether GAM accepts <code>WHERE name LIKE '%term%'</code> on
                getCmsMetadataValuesByStatement. If yes, we can replace the 1-3 min bulk paginate
                with per-candidate search. Tries both <code>name</code> and <code>valueName</code>
                column variants.
              </p>
              <div style={styles.inlineRow}>
                <label style={styles.inlineLabel}>Probe term</label>
                <input
                  type="text"
                  value={pqlProbeTerm}
                  onChange={(e) => setPqlProbeTerm(e.target.value)}
                  placeholder="e.g. psg"
                  style={styles.input}
                />
                <button
                  onClick={handleRunPqlProbe}
                  disabled={pqlProbeRunning || !pqlProbeTerm.trim()}
                  style={styles.primaryButton}
                >
                  {pqlProbeRunning ? 'Probing…' : 'Run probe'}
                </button>
              </div>
              {pqlProbeError && (
                <div style={{ ...styles.errorBanner, marginTop: 12 }}>{pqlProbeError}</div>
              )}
              {pqlProbeResult && (
                <ul style={styles.diagList}>
                  {pqlProbeResult.keywordsKeyId && (
                    <li style={{ ...styles.subText, paddingLeft: 0 }}>
                      Resolved keywords keyId: <code>{pqlProbeResult.keywordsKeyId}</code>
                    </li>
                  )}
                  {pqlProbeResult.attempts.map((a) => (
                    <li key={a.label} style={styles.diagItem}>
                      <StatusPill variant={a.success ? 'live' : 'flagged'} noDot>
                        {a.success ? 'pass' : 'fail'}
                      </StatusPill>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          <code>{a.label}</code>
                          {a.success && typeof a.totalResultSetSize === 'number' && (
                            <> — {a.rowCount} returned, {a.totalResultSetSize.toLocaleString()} total</>
                          )}
                        </div>
                        <div style={styles.subText}>
                          <code>{a.query}</code>
                        </div>
                        {a.errorMessage && (
                          <div style={{ ...styles.subText, color: 'var(--danger)' }}>
                            {a.errorMessage}
                          </div>
                        )}
                        {a.firstRowKeys && a.firstRowKeys.length > 0 && (
                          <div style={styles.subText}>
                            First-row keys: [{a.firstRowKeys.join(', ')}]
                          </div>
                        )}
                        {a.firstRowSample != null && (
                          <details style={{ marginTop: 6 }}>
                            <summary style={styles.dropSummary}>Show sample row</summary>
                            <pre style={styles.diagPre}>
                              {JSON.stringify(a.firstRowSample, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border-1)' }}>
              <h3 style={{ ...styles.sectionTitle, fontSize: 14 }}>
                ContentService PQL probe
              </h3>
              <p style={styles.sectionSubtitle}>
                Tests several PQL syntaxes for filtering <code>Content</code> by
                <code> cmsMetadataValueIds</code>. Forces a column-list error first so
                GAM tells us the valid columns, then tries CONTAINS / IN / CONTAINS_ANY
                / CONTAINS_ALL variants against a known-good value id.
              </p>
              <div style={styles.inlineRow}>
                <button
                  onClick={handleRunContentProbe}
                  disabled={contentProbeRunning}
                  style={styles.primaryButton}
                >
                  {contentProbeRunning ? 'Probing…' : 'Run content probe'}
                </button>
              </div>
              {contentProbeError && (
                <div style={{ ...styles.errorBanner, marginTop: 12 }}>{contentProbeError}</div>
              )}
              {contentProbeResult && (
                <ul style={styles.diagList}>
                  {contentProbeResult.sampleValueId && (
                    <li style={{ ...styles.subText, paddingLeft: 0 }}>
                      Probed using value id <code>{contentProbeResult.sampleValueId}</code>
                    </li>
                  )}
                  {contentProbeResult.attempts.map((a) => (
                    <li key={a.label} style={styles.diagItem}>
                      <StatusPill variant={a.success ? 'live' : 'flagged'} noDot>
                        {a.success ? 'pass' : 'fail'}
                      </StatusPill>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{a.label}</div>
                        <div style={styles.subText}>
                          <code>{a.query}</code>
                        </div>
                        {a.success && typeof a.totalResultSetSize === 'number' && (
                          <div style={styles.subText}>
                            {a.rowCount} returned, {a.totalResultSetSize.toLocaleString()} total
                          </div>
                        )}
                        {a.errorMessage && (
                          <div style={{ ...styles.subText, color: 'var(--danger)' }}>
                            {a.errorMessage}
                          </div>
                        )}
                        {a.firstRowKeys && a.firstRowKeys.length > 0 && (
                          <div style={styles.subText}>
                            First-row keys: [{a.firstRowKeys.join(', ')}]
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
      )}

      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <h2 style={styles.sectionTitle}>Recent bundle activity</h2>
            <p style={styles.sectionSubtitle}>Last 20 bundle creates (audit log).</p>
          </div>
          <button onClick={() => setAuditOpen((v) => !v)} style={styles.linkButton}>
            {auditOpen ? 'Hide' : 'Show'}
          </button>
        </div>
        {auditOpen && (
          audit.length === 0 ? (
            <div style={styles.emptyText}>No bundles created yet.</div>
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Bundle</th>
                    <th style={styles.th}>Topic</th>
                    <th style={styles.th}># videos</th>
                    <th style={styles.th}>Created by</th>
                    <th style={styles.th}>When</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((e) => (
                    <tr key={e.id} style={styles.tr}>
                      <td style={styles.td}>
                        {e.bundleName}
                        {e.dryRun && <span style={styles.dryTag}> dry-run</span>}
                        {e.status === 'attempting' && <span style={styles.dryTag}> attempting</span>}
                        {e.status === 'failed' && <span style={styles.dryTag}> failed</span>}
                      </td>
                      <td style={styles.td}>{e.topic || '—'}</td>
                      <td style={styles.td}>
                        {e.suggestedContentIds?.length ?? e.contentIdCount ?? 0}
                      </td>
                      <td style={styles.td}>{e.createdBy}</td>
                      <td style={styles.td}>{new Date(e.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  dryRunBanner: {
    background: 'var(--warning-soft)',
    border: '1px solid var(--cg-clay-200)',
    color: 'var(--warning)',
    padding: '10px 14px',
    borderRadius: 'var(--radius-md)',
    fontSize: 13,
    fontWeight: 500,
  },
  cacheBannerInfo: {
    background: 'var(--info-soft)',
    border: '1px solid var(--cg-teal-200)',
    color: 'var(--info)',
    padding: '10px 14px',
    borderRadius: 'var(--radius-md)',
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  cacheBannerSubtle: {
    background: 'var(--cg-neutral-50)',
    border: '1px solid var(--border-1)',
    color: 'var(--fg-2)',
    padding: '8px 14px',
    borderRadius: 8,
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  section: {
    background: 'var(--bg-surface-1)',
    border: '1px solid var(--border-1)',
    borderRadius: 8,
    padding: 20,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
    flexWrap: 'wrap' as const,
    gap: 12,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  sectionSubtitle: {
    margin: '4px 0 0 0',
    fontSize: 12,
    color: 'var(--fg-2)',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid var(--border-1)',
    borderRadius: 6,
    fontSize: 14,
    fontFamily: 'var(--font-sans)',
    background: 'var(--bg-app)',
    color: 'var(--fg-1)',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
  },
  input: {
    padding: '8px 12px',
    border: '1px solid var(--border-1)',
    borderRadius: 6,
    fontSize: 13,
    fontFamily: 'var(--font-sans)',
    background: 'var(--bg-app)',
    color: 'var(--fg-1)',
    flex: 1,
    boxSizing: 'border-box' as const,
  },
  inlineRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 10,
  },
  inlineLabel: {
    fontSize: 12,
    color: 'var(--fg-2)',
    width: 220,
  },
  buttonRow: {
    display: 'flex',
    gap: 8,
    marginTop: 14,
  },
  primaryButton: {
    padding: '9px 16px',
    border: 'none',
    borderRadius: 6,
    background: 'var(--accent)',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
  },
  secondaryButton: {
    padding: '9px 14px',
    border: '1px solid var(--border-1)',
    borderRadius: 6,
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
  },
  linkButton: {
    background: 'transparent',
    border: 'none',
    color: 'var(--accent)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    padding: 0,
    fontFamily: 'var(--font-sans)',
  },
  errorBanner: {
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    border: '1px solid var(--danger)',
  },
  successBanner: {
    background: 'var(--success-soft)',
    color: 'var(--success)',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    border: '1px solid var(--success)',
  },
  successLink: {
    color: 'var(--success)',
    textDecoration: 'underline',
    fontWeight: 600,
  },
  chipAddRow: {
    display: 'flex',
    gap: 8,
    marginTop: 10,
    alignItems: 'center',
  },
  chipAddInput: {
    flex: 1,
    padding: '8px 12px',
    border: '1px solid var(--cg-neutral-200)',
    borderRadius: 6,
    fontSize: 13,
    fontFamily: 'var(--font-sans)',
  },
  chipAddError: {
    marginTop: 6,
    fontSize: 12,
    color: 'var(--danger)',
  },
  chipWrap: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 6,
  },
  dropDetails: {
    marginTop: 12,
    fontSize: 12,
  },
  dropSummary: {
    cursor: 'pointer',
    color: 'var(--fg-2)',
  },
  dropList: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 6,
    marginTop: 8,
  },
  dropChip: {
    padding: '3px 8px',
    border: '1px dashed var(--border-1)',
    borderRadius: 'var(--radius-pill)',
    color: 'var(--fg-3)',
    fontSize: 12,
  },
  tableWrapper: tableStyles.wrapScroll,
  table: tableStyles.table,
  th: tableStyles.th,
  tr: {},
  td: {
    ...tableStyles.td,
    verticalAlign: 'middle' as const,
  },
  subText: {
    fontSize: 11,
    color: 'var(--fg-2)',
    marginTop: 2,
  },
  emptyText: {
    fontSize: 13,
    color: 'var(--fg-2)',
    padding: '12px 0',
  },
  dryTag: {
    marginLeft: 6,
    fontSize: 11,
    color: 'var(--warning)',
    background: 'var(--warning-soft)',
    padding: '1px 6px',
    borderRadius: 'var(--radius-pill)',
  },
  adminTag: {
    marginLeft: 8,
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'var(--fg-2)',
    background: 'var(--cg-neutral-50)',
    padding: '2px 8px',
    borderRadius: 'var(--radius-pill)',
    verticalAlign: 'middle',
  },
  diagOverallPass: {
    padding: '8px 12px',
    borderRadius: 8,
    background: 'var(--success-soft)',
    color: 'var(--success)',
    fontSize: 13,
    border: '1px solid var(--success)',
  },
  diagOverallFail: {
    padding: '8px 12px',
    borderRadius: 8,
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    fontSize: 13,
    border: '1px solid var(--danger)',
  },
  diagList: {
    listStyle: 'none' as const,
    padding: 0,
    margin: '12px 0 0 0',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 10,
  },
  diagItem: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start' as const,
    padding: '8px 10px',
    border: '1px solid var(--border-1)',
    borderRadius: 6,
  },
  diagPre: {
    background: 'var(--cg-neutral-100)',
    padding: 8,
    borderRadius: 6,
    fontSize: 11,
    overflowX: 'auto' as const,
    maxHeight: 200,
    margin: '4px 0 0 0',
    fontFamily: 'var(--font-mono)',
  },
};

export default BundleGeneratorTab;
